import React, { useContext } from "react";
import { Container, Stack, Flex, Box, Heading, Text } from "@chakra-ui/react";
import CtaButton from "./ctaButton.tsx";
import { StaticImage } from "gatsby-plugin-image";
import ProblemStatement from "./problemStatement.tsx";
import { LanguageContext } from "../components/LanguageProvider";
import { messages } from "../intl/config"; // Import tłumaczeń

interface SectionItem {
  title: string;
  text: string;
  textOnLeft: boolean;
  color: string;
  cta: string;
  component: string | JSX.Element;
}

const Sections = () => {
  const { locale = "en" } = useContext(LanguageContext); // Pobieramy bieżący język z kontekstu
  const translations = messages[locale] || {}; // Wybieramy tłumaczenia na podstawie języka

  const SECTIONS: Array<SectionItem> = [
    {
      title: translations["sections.section1.title"],
      text: translations["sections.section1.text"],
      component: <ProblemStatement />,
      textOnLeft: true,
      color: "gray.700",
      cta: translations["sections.section1.cta"],
    },
    {
      title: translations["sections.section2.title"],
      text: translations["sections.section2.text"],
      component: (
        <StaticImage
          src="../images/nasza_specjalizacja.png"
          alt={translations["sections.section2.alt"]}
          formats={["auto", "webp", "avif"]}
          placeholder="blurred"
          layout="constrained"
        />
      ),
      textOnLeft: false,
      color: "gray.700",
      cta: translations["sections.section2.cta"],
    },
  ];

  return (
    <Stack spacing={0} direction={"column"} alignItems={"center"} id="sections">
      {SECTIONS.map((section, index) => (
        <Section
          key={index}
          title={section.title}
          text={section.text}
          textOnLeft={section.textOnLeft}
          color={section.color}
          cta={section.cta}
          component={section.component}
        />
      ))}
    </Stack>
  );
};

const Section = ({
  title,
  text,
  textOnLeft,
  color,
  cta,
  component,
}: SectionItem) => {
  return (
    <Container maxW={"7xl"} px={{ base: 4, md: 12 }}>
      <Stack
        align={"center"}
        spacing={{ base: 8, md: 10 }}
        py={{ base: 12, md: 20 }}
        direction={{ base: "column", md: "row" }}
      >
        <Stack
          flex={1}
          spacing={{ base: 5, md: 8 }}
          alignSelf={"flex-start"}
          order={{ base: 1, md: textOnLeft ? 1 : 2 }}
        >
          <Heading
            lineHeight={1.1}
            fontWeight={600}
            fontSize={{ base: "2xl", sm: "3xl", lg: "5xl" }}
            marginBottom={2}
            marginTop={4}
          >
            <Text as={"span"} color={color}>
              {title}
            </Text>
          </Heading>
          <Text color={"gray.600"} fontSize={{ base: "md", sm: "md", lg: "xl" }}>
            {text}
          </Text>
          <CtaButton text={cta} />
        </Stack>
        <Flex
          flex={1}
          justify={"center"}
          align={"center"}
          position={"relative"}
          w={"full"}
          order={{ base: 2, md: textOnLeft ? 2 : 1 }}
          dir={textOnLeft ? "ltr" : "rtl"}
        >
          <Box
            position={"relative"}
            rounded={"xl"}
            boxShadow={"lg"}
            width={"full"}
            overflow={"hidden"}
          >
            {component}
          </Box>
        </Flex>
      </Stack>
    </Container>
  );
};

export default Sections;
